import { Controller } from "@hotwired/stimulus"

/**
 * Ad Type Selection Controller
 *
 * Handles ad type (subscription plan) selection in wizard Step 1.
 * Brain Hacking: Uses "광고 유형" framing instead of "구독 플랜".
 *
 * Features:
 * - Plan selection with hidden input sync
 * - Self-diagnosis dialog (Brain Hacking: 납득 강화)
 * - Comparison table toggle
 *
 * Styling is handled by CSS peer selectors (peer-checked:border-*).
 *
 * Targets:
 *   - planInput: Hidden input storing selected plan value
 *   - trialCard: Trial plan card label
 *   - growthCard: Growth plan card label
 *   - successCard: Success plan card label
 *   - comparisonToggle: Comparison section toggle button
 *   - comparisonArrow: Arrow icon for toggle state
 *   - comparisonTable: Comparison table container
 *
 * Values:
 *   - currentPlan: User's current subscription plan (for future upgrade flow)
 */
export default class extends Controller {
  static targets = [
    "planInput",
    "trialCard",
    "growthCard",
    "successCard",
    "comparisonToggle",
    "comparisonArrow",
    "comparisonTable"
  ]
  static values = {
    currentPlan: { type: String, default: "none" }
  }

  /**
   * Handle plan selection from radio button change
   * Syncs selected value to hidden input for form submission
   * @param {Event} event - Change event from radio input
   */
  selectPlan(event) {
    const selectedPlan = event.target.value

    if (this.hasPlanInputTarget) {
      this.planInputTarget.value = selectedPlan
    }

    // 선택된 카드를 화면 중앙으로 스크롤
    const cardTarget = this.getCardTarget(selectedPlan)
    if (cardTarget) {
      // 약간의 딜레이로 선택 애니메이션 후 스크롤
      setTimeout(() => {
        cardTarget.scrollIntoView({ behavior: "smooth", block: "center" })
      }, 100)
    }
  }

  /**
   * Open diagnosis dialog (bottom sheet)
   * Brain Hacking: 납득 - user self-selects their need
   */
  openDiagnosisDialog() {
    window.dispatchEvent(new CustomEvent('dialog:open', {
      detail: { dialogId: 'diagnosis-dialog' }
    }))
  }

  /**
   * Handle diagnosis selection from dialog
   * @param {Event} event - Click event from dialog option
   */
  selectDiagnosis(event) {
    const plan = event.currentTarget.dataset.plan

    // Close dialog first
    window.dispatchEvent(new CustomEvent('dialog:close', {
      detail: { dialogId: 'diagnosis-dialog' }
    }))

    // Highlight corresponding card after dialog closes
    setTimeout(() => {
      this.highlightPlanByName(plan)
    }, 350) // After dialog close animation (300ms)
  }

  /**
   * Highlight plan card by name
   * @param {string} planName - trial, growth, or success
   */
  highlightPlanByName(planName) {
    this.resetCardHighlights()

    const cardTarget = this.getCardTarget(planName)
    if (cardTarget) {
      cardTarget.classList.add("ring-2", "ring-offset-2")

      switch (planName) {
        case "trial":
          cardTarget.classList.add("ring-charcoal-400")
          break
        case "growth":
          cardTarget.classList.add("ring-orange-400")
          break
        case "success":
          cardTarget.classList.add("ring-blue-400")
          break
      }

      cardTarget.scrollIntoView({ behavior: "smooth", block: "center" })

      setTimeout(() => {
        this.resetCardHighlights()
      }, 2000)
    }
  }

  /**
   * Toggle comparison table visibility
   * Brain Hacking: 납득 - detailed comparison for informed decision
   */
  toggleComparison() {
    if (!this.hasComparisonTableTarget) return

    const isHidden = this.comparisonTableTarget.classList.contains("hidden")

    if (isHidden) {
      // Show table
      this.comparisonTableTarget.classList.remove("hidden")
      if (this.hasComparisonArrowTarget) {
        this.comparisonArrowTarget.classList.add("rotate-180")
      }
    } else {
      // Hide table
      this.comparisonTableTarget.classList.add("hidden")
      if (this.hasComparisonArrowTarget) {
        this.comparisonArrowTarget.classList.remove("rotate-180")
      }
    }
  }

  // ============================================================================
  // Private Helpers
  // ============================================================================

  /**
   * Get card target by plan name
   * @param {string} planName - trial, growth, or success
   * @returns {Element|null}
   */
  getCardTarget(planName) {
    switch (planName) {
      case "trial":
        return this.hasTrialCardTarget ? this.trialCardTarget : null
      case "growth":
        return this.hasGrowthCardTarget ? this.growthCardTarget : null
      case "success":
        return this.hasSuccessCardTarget ? this.successCardTarget : null
      default:
        return null
    }
  }

  /**
   * Reset all card highlight styles
   */
  resetCardHighlights() {
    const ringClasses = [
      "ring-2",
      "ring-offset-2",
      "ring-charcoal-400",
      "ring-orange-400",
      "ring-blue-400"
    ]

    if (this.hasTrialCardTarget) {
      this.trialCardTarget.classList.remove(...ringClasses)
    }
    if (this.hasGrowthCardTarget) {
      this.growthCardTarget.classList.remove(...ringClasses)
    }
    if (this.hasSuccessCardTarget) {
      this.successCardTarget.classList.remove(...ringClasses)
    }
  }
}
